import clsx from 'clsx'
import { FormikContext, useFormik } from 'formik'
import { FC, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { AnnualSubscription } from './components/AnnualSubscription'
import { MonthlySubscription } from './components/MonthlySubscription'
import { getPaymentSectionInitialValues } from './helpers/get-payment-section-initial-values'
import { PaymentSectionForm } from './payment-section.interface'
import styles from './payment-section.module.scss'
import { LocalStorageService } from '../../../../../utils/local-storage-service/local-storage-service'
import { StepperKey } from '../../constants/auth.constants'
import { calculatePricing } from '../../helpers/calculatePricing'

type PaymentSectionProps = {
  initialValues?: PaymentSectionForm
  flatData: any
}

export const PaymentSection: FC<PaymentSectionProps> = forwardRef(({ initialValues }, ref) => {
  const { t } = useTranslation()
  const formik = useFormik<PaymentSectionForm>({
    initialValues: getPaymentSectionInitialValues(initialValues),
    onSubmit: () => {},
  })

  useImperativeHandle(ref, () => ({
    getValues: () => {
      return {
        errors: formik.validateForm(),
        values: formik.values,
      }
    },
  }))

  const handleYearChange = (state: boolean) => {
    if (!formik.values.year) {
      formik.setFieldValue('year', state)
      formik.setFieldValue('month', !state)
    }
  }

  const handleMonthChange = (state: boolean) => {
    if (!formik.values.month) {
      formik.setFieldValue('month', state)
      formik.setFieldValue('year', !state)
    }
  }

  const companyData = LocalStorageService.get('userData')?.[StepperKey.CompanyConfig]

  const { needCustomPlan, monthlyPrice, annualPrice, amountSaved, min, max } = calculatePricing(companyData)

  return (
    <FormikContext.Provider value={formik as any}>
      <h2 className="mt-3 mb-5">{t('onboarding.payment.header')}</h2>
      <div className="step-wrapper d-flex align-items-center flex-wrap justify-content-between pb-4 mt-4">
        <AnnualSubscription
          amountSaved={amountSaved}
          annualPrice={annualPrice}
          employeesSlots={{ min, max }}
          initialState={formik.values.year}
          monthlyPrice={monthlyPrice}
          needCustomPlan={needCustomPlan}
          onStateChange={handleYearChange}
        />
        <MonthlySubscription
          employeesSlots={{ min, max }}
          initialState={formik.values.month}
          monthlyPrice={monthlyPrice}
          needCustomPlan={needCustomPlan}
          onStateChange={handleMonthChange}
        />
      </div>
      {needCustomPlan ? null : (
        <div className="text-end">
          <p className="mb-0">{formik.values.year ? `${annualPrice / 12} x 12 = ${annualPrice}` : `${monthlyPrice} x 1 = ${monthlyPrice}`}</p>
          <p className="fw-bold">
            {t('onboarding.payment.total')}:
            <span className={clsx(styles.total, 'color-primary')}>{formik.values.year ? annualPrice : monthlyPrice} ₽</span>
          </p>
        </div>
      )}
    </FormikContext.Provider>
  )
})
